import { applyTheme as themeRegistryApplyTheme, syncLegacyThemeStorage, getTheme, DEFAULT_THEME_ID } from '../config/themeRegistry';
import { hasAuthToken } from '../utils/sessionIdentity';
import { defineStore } from 'pinia';
import i18n from '../i18n';
import { ref, computed, watch } from 'vue';

export type OmniMode = 'chat' | 'dev' | 'media' | 'zen';
/**
 * 主题 id：两个内置档有字面量补全；`(string & {})` 打开派生包注入的主题
 * （themeRegistry.registerTheme 注册的行业主题）—— 联合写死成两个字面量时，
 * 注册表是"预留接口"但 store 永远装不下派生主题 id，T2-6 的"派生包可注入色板"
 * 在端到端上不可达。
 */
export type OmniTheme = 'cyber' | 'minimal' | (string & {});
export type DockMode = 'desktop' | 'admin';

// 导航追踪类型
export interface RecentVisit {
  path: string;
  hubId: string;
  label: string;
  timestamp: number;
}

const RECENTS_KEY = 'omni_recents';
const FAVORITES_KEY = 'omni_favorites';
const MAX_RECENTS = 20;

function loadRecents(): RecentVisit[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((r: any) => r.path && r.hubId && r.label && r.timestamp);
  } catch { return []; }
}

function saveRecents(recents: RecentVisit[]) {
  localStorage.setItem(RECENTS_KEY, JSON.stringify(recents));
}

function loadFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((p: any) => typeof p === 'string'));
  } catch { return new Set(); }
}

function saveFavorites(favs: Set<string>) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify([...favs]));
}

export const useOmniStore = defineStore('omni', () => {
    // 默认主题：科技宋代美学（minimal=浅色/宣纸白瓷，cyber=暗色/徽墨玄渊）。
    // 「换默认值」这件事**只有一个归属方**：index.html 的首屏脚本（版本戳
    // omni_theme_version + omni_theme_explicit 标记 —— 动过主题开关的用户不跟）。
    // 这里只读不写。
    //
    // [2026-10-05] 原先这里另有两套迁移（THEME_DEFAULT_VERSION / CURRENT_THEME_VER），
    // 其中后者在版本戳变化时**无条件**把 omni_theme 覆盖成 minimal、根本不看 explicit
    // 标记 —— 与首屏脚本的规矩正相反。两份版本常量（index.html 与这里）一旦漂移
    // （而"改默认值"就是靠改它们），用户选过的主题会被静默改掉；
    // e2e（tests/e2e/tech-song-nexus-shell.spec.ts）里那句"否则 omniStore 的迁移把
    // 主题重置回 minimal"就是绕它。两套并存 = 一条永远打不准、又随时会打偏的枪，删。
    //
    // 未知 id（卸载后的派生主题）不在这里校验：注册可能晚于 store 创建，
    // 校验落在写口 setTheme 与 applyTheme 的回落里（DOM 与状态一起回落）。
    const theme = ref<OmniTheme>((localStorage.getItem('omni_theme') as OmniTheme) || DEFAULT_THEME_ID);
    // 核心状态
    const mode = ref<OmniMode>('chat');
    const isSidebarOpen = ref(true);
    const isHudVisible = ref(false);
    
    // 全局掩码与权限状态 (Global PII Masking & Policy)
    const isPiiMaskingEnabled = ref(localStorage.getItem('omni_pii_masking') === 'true');
    function setPiiMasking(val: boolean) {
        isPiiMaskingEnabled.value = val;
        localStorage.setItem('omni_pii_masking', String(val));
    }

    // Dock 模式 (桌面极简 / 管理完整)
    const dockMode = ref<DockMode>(
        (localStorage.getItem('omni_dock_mode') as DockMode) || 'desktop'
    );
    // Dock 抽屉固定钉选状态 (是否占用独立布局宽度并推挤主内容)
    const isDockDrawerPinned = ref(
        localStorage.getItem('omni_dock_drawer_pinned') === 'true'
    );
    // 当前激活的 Dock Hub 抽屉
    const activeDockHubId = ref<string | null>(null);

    function toggleDockDrawerPin() {
        isDockDrawerPinned.value = !isDockDrawerPinned.value;
        localStorage.setItem('omni_dock_drawer_pinned', String(isDockDrawerPinned.value));
    }

    function setActiveDockHub(hubId: string | null) {
        activeDockHubId.value = hubId;
    }
    // SocialSidebar 可见性
    const isSocialSidebarOpen = ref(
        localStorage.getItem('omni_social_sidebar') !== 'false'
    );

    const isBrowser = typeof localStorage !== 'undefined';
    const isDev = import.meta.env.DEV;

    // canToggleDockMode: 由服务端 securityContext 驱动（非 AccountTier 硬编码）
    const externalAuthzUnlocked = ref(false);
    function setAuthzUnlocked(val: boolean) {
        externalAuthzUnlocked.value = val;
    }

    // 服务器集群端千人千面、预设分组——客户端只读取服务端返回的权限信号
    const canToggleDockMode = computed(() => {
        try {
            if (externalAuthzUnlocked.value) return true;
            // DEV 模式：任何已登录用户自动获得管理端切换权限
            // [IC-53] 同集合的析取走唯一实现（语义不变）
            if (isDev && isBrowser && hasAuthToken()) return true;
        } catch (_) {
            return dockMode.value === 'admin';
        }
        return false;
    });

    // effectiveDockMode: 无服务端管理权限 → 锁定 desktop
    const effectiveDockMode = computed<DockMode>(() => {
        if (!canToggleDockMode.value) return 'desktop';
        return dockMode.value;
    });

    function toggleDockMode() {
        if (!canToggleDockMode.value) {
            window.dispatchEvent(new CustomEvent('global-toast', {
                detail: {
                    message: i18n.global.t('permission.admin_required'),
                    type: 'warning'
                }
            }));
            return;
        }
        dockMode.value = dockMode.value === 'desktop' ? 'admin' : 'desktop';
        localStorage.setItem('omni_dock_mode', dockMode.value);
    }

    function toggleSocialSidebar() {
        isSocialSidebarOpen.value = !isSocialSidebarOpen.value;
        localStorage.setItem('omni_social_sidebar', String(isSocialSidebarOpen.value));
    }

    // 桌面客户端感知状态
    type DesktopClientStatus = 'probing' | 'connected' | 'not_found' | 'web_only';
    const desktopClientStatus = ref<DesktopClientStatus>('probing');
    const desktopClientDismissed = ref(false);

    // 输入状态
    const inputState = ref({
        text: '',
        isRecording: false,
        attachments: [] as File[],
    });

    // 叠加层状态
    const overlays = ref({
        pluginPanel: false,
        settings: false,
        genesis: false,
        codeDrawer: false,
        ecgPanel: false,  // [ECG] 对外能力网关面板
    });

    // Code Drawer Context
    const codeDrawerContext = ref({
        filePath: '',
        worktree: '.'
    });

    // Centralized UI toggles replacing legacy dispatchEvent
    const isHelpCenterOpen = ref(false);
    const isFocusModeOpen = ref(false);
    const isFluidIslandMode = ref(false);
    const isGoalModeOpen = ref(false);
    // [类型修复+功能补全] /system-control 路由（SystemControlCenterWrapper）此前调用
    // 不存在的 toggleSystemControl 而静默失效——状态上移到 store 并由 OmniLayout 消费
    const isControlCenterOpen = ref(false);

    function toggleHelpCenter() {
        isHelpCenterOpen.value = !isHelpCenterOpen.value;
    }
    function toggleSystemControl() {
        isControlCenterOpen.value = !isControlCenterOpen.value;
    }
    function toggleFocusMode() {
        isFocusModeOpen.value = !isFocusModeOpen.value;
    }
    function setFluidIslandMode(val: boolean) {
        isFluidIslandMode.value = val;
    }
    function toggleGoalMode() {
        isGoalModeOpen.value = !isGoalModeOpen.value;
    }
    // [ECG] 对外能力网关面板开关
    function toggleEcgPanel() {
        overlays.value.ecgPanel = !overlays.value.ecgPanel;
    }

    // 窗口边界感知 (全局响应适应排版)
    const storedWindows = localStorage.getItem('omni-active-windows');
    const activeWindows = ref<Record<string, { x: number, y: number, w: number, h: number }>>(storedWindows ? JSON.parse(storedWindows) : {});
    
    watch(activeWindows, (newVal) => {
        localStorage.setItem('omni-active-windows', JSON.stringify(newVal));
    }, { deep: true });

    function setWindowRect(id: string, rect: { x: number, y: number, w: number, h: number }) {
        activeWindows.value = { ...activeWindows.value, [id]: rect };
    }
    function removeWindowRect(id: string) {
        const next = { ...activeWindows.value };
        delete next[id];
        activeWindows.value = next;
    }

    // Registry Packages (持久化存储)
    const storedRegistry = localStorage.getItem('omni-registry-packages');
    const registryPackages = ref<any[]>(storedRegistry ? JSON.parse(storedRegistry) : []);
    watch(registryPackages, (newVal: any) => {
        localStorage.setItem('omni-registry-packages', JSON.stringify(newVal));
    }, { deep: true });
    
    // Pending Registry Install Query
    const pendingRegistryInstallQuery = ref<string>('');
    const pendingAuthQuery = ref<string>('');
    const pendingBoardIntent = ref<any>(null);
    const activeNodeId = ref<string>('');

    // 物理元数据采样
    // [mock→真实] 每机唯一生物识别 ID（持久化生成），替代全局写死的假编号
    const bioId = ref((() => {
        try {
            let id = localStorage.getItem('omni_bio_id');
            if (!id) {
                id = '123ALL-GEN-' + Math.random().toString(36).slice(2, 8).toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
                localStorage.setItem('omni_bio_id', id);
            }
            return id;
        } catch { return '123ALL-GEN-LOCAL'; }
    })());
    // 系统熵：真源是 `/authz/monitor/entropy`（RolloutTopology 拉取后写入）。
    // 初值必须是 null（未知），此前写死 0.24 —— 一个从未测过的读数会被
    // NeuralVisualizer 当成真实熵渲染（D-22：熵读数必须有真源或显式"未知"）。
    const systemEntropy = ref<number | null>(null);

    // Swarm Resonance (Global Telemetry)
    const swarmResonance = ref<{
        fluid_impedance: number;
        field_strength_injected: number;
        is_redshift: boolean;
    } | null>(null);

    // ─── 导航追踪：最近访问 & 收藏夹 ───
    const recents = ref<RecentVisit[]>(loadRecents());
    const _favorites = ref<Set<string>>(loadFavorites());
    const defaultHomePage = ref<string>(localStorage.getItem('omni_default_home') || '');

    function setDefaultHomePage(path: string) {
        if (defaultHomePage.value === path) {
            defaultHomePage.value = '';
            localStorage.removeItem('omni_default_home');
        } else {
            defaultHomePage.value = path;
            localStorage.setItem('omni_default_home', path);
        }
    }


    function recordVisit(hubId: string, path: string, label: string) {
        const now = Date.now();
        const filtered = recents.value.filter(r => r.path !== path);
        filtered.unshift({ path, hubId, label, timestamp: now });
        if (filtered.length > MAX_RECENTS) {
            filtered.splice(MAX_RECENTS);
        }
        recents.value = filtered;
        saveRecents(filtered);
    }

    function toggleFavorite(path: string) {
        const favs = new Set(_favorites.value);
        if (favs.has(path)) {
            favs.delete(path);
        } else {
            favs.add(path);
        }
        _favorites.value = favs;
        saveFavorites(favs);
    }

    function clearRecents() {
        recents.value = [];
        saveRecents([]);
    }

    function isFavorite(path: string): boolean {
        return _favorites.value.has(path);
    }

    // 按 hub 过滤最近项，不传 hubId 返回全部
    function recentItems(hubId?: string): RecentVisit[] {
        if (!hubId) return recents.value.slice(0, 10);
        return recents.value.filter(r => r.hubId === hubId).slice(0, 5);
    }

    // 按访问频率聚合排序
    const popularItems = computed<RecentVisit[]>(() => {
        const counts = new Map<string, { count: number; item: RecentVisit }>();
        for (const r of recents.value) {
            const existing = counts.get(r.path);
            if (existing) {
                existing.count++;
            } else {
                counts.set(r.path, { count: 1, item: r });
            }
        }
        return [...counts.values()]
            .sort((a, b) => b.count - a.count)
            .slice(0, 10)
            .map(c => c.item);
    });

    // Actions
    function setMode(newMode: OmniMode) {
        mode.value = newMode;
    }

    function toggleOverlay(overlay: keyof typeof overlays.value) {
        overlays.value[overlay] = !overlays.value[overlay];
    }

    // 「这一档暗不暗」问注册表，不问 id 字面量：派生包注入的主题由包声明 mode，
    // 用 `=== 'cyber'` 判会把暗色派生主题报成亮色（坞里主题行的签、设置页的开关都吃这个读数）。
    const isDark = computed(() => getTheme(theme.value)?.mode === 'dark');

    // [T2-6] DOM 侧应用与 legacy 三键同步单源化至 themeRegistry；
    // 派生包可通过 registerTheme() 注入行业主题（平台不改，派生扩展）。
    function applyTheme() {
        themeRegistryApplyTheme(theme.value);
        syncLegacyThemeStorage(theme.value);
    }

    // 快捷开关的唯一语义：切到"另一档"的内置主题（暗 → 默认浅，亮 → cyber 暗）。
    // 必须走 setTheme（唯一写口）—— 原先这里自己写 localStorage，漏了 explicit 标记，
    // 于是"从命令面板/坞里切过主题"的用户在下一次换默认值时被静默改回去：
    // 三个入口、两条写路径，其中一条不认账，是最难查的那类不一致。
    function toggleTheme() {
        setTheme(isDark.value ? DEFAULT_THEME_ID : 'cyber');
    }

    // [A4 fix] Explicit setter for single-source-of-truth theme management.
    function setTheme(t: OmniTheme) {
        // 未知 id 失败关闭到默认主题：派生主题若尚未注册（或已卸载），
        // 状态与 DOM 必须落在同一处，不能一个记 id、一个落默认。
        const def = getTheme(t);
        theme.value = def ? t : DEFAULT_THEME_ID;
        localStorage.setItem('omni_theme', theme.value);
        // 首屏脚本只认两个内置 id，读不懂派生主题；给它一份"这一档暗不暗"的读数，
        // 否则暗色派生主题会在首屏先闪一下亮色（FOUC），再被 store 校正回来。
        localStorage.setItem('omni_theme_mode', getTheme(theme.value)?.mode === 'dark' ? 'dark' : 'light');
        // index.html 的首屏脚本在版本号变化时会把主题重置成当期默认值，好让改版
        // 能推给还没做过选择的人。但它光看 omni_theme 分不清「这是默认值」还是
        // 「用户自己挑的」—— 于是选了宣纸白的人，每次发版都会被静默改回玄渊黑。
        // 这个标记就是那条分界线：只要用户动过开关，重置就绕开他。
        localStorage.setItem('omni_theme_explicit', '1');
        applyTheme();
    }

    function setDesktopClientStatus(status: DesktopClientStatus) {
        desktopClientStatus.value = status;
    }

    function dismissDesktopBanner() {
        desktopClientDismissed.value = true;
        desktopClientStatus.value = 'web_only';
    }

    // Initialize theme immediately on store creation
    applyTheme();

    return {
        mode,
        theme,
        isSidebarOpen,
        isHudVisible,
        isPiiMaskingEnabled,
        setPiiMasking,
        dockMode,
        isDockDrawerPinned,
        activeDockHubId,
        toggleDockDrawerPin,
        setActiveDockHub,
        isSocialSidebarOpen,
        effectiveDockMode,
        canToggleDockMode,
        toggleDockMode,
        toggleSocialSidebar,
        isHelpCenterOpen,
        isFocusModeOpen,
        isFluidIslandMode,
        isGoalModeOpen,
        isControlCenterOpen,
        toggleHelpCenter,
        toggleSystemControl,
        toggleFocusMode,
        setFluidIslandMode,
        toggleGoalMode,
        toggleEcgPanel,
        inputState,
        overlays,
        codeDrawerContext,
        activeWindows,
        setWindowRect,
        removeWindowRect,
        bioId,
        systemEntropy,
        swarmResonance,
        isDark,
        desktopClientStatus,
        desktopClientDismissed,
        setMode,
        toggleOverlay,
        toggleTheme,
        setTheme,
        setDesktopClientStatus,
        dismissDesktopBanner,
        openCodeDrawer: (filePath?: string, worktree?: string) => {
            overlays.value.codeDrawer = true;
            if (filePath) codeDrawerContext.value.filePath = filePath;
            if (worktree) codeDrawerContext.value.worktree = worktree;
        },
        closeCodeDrawer: () => {
            overlays.value.codeDrawer = false;
        },
        // ─── Registry Packages ───
        registryPackages,
        pendingRegistryInstallQuery,
        pendingAuthQuery,
        pendingBoardIntent,
        activeNodeId,
        // ─── 导航追踪 ───
        recents,
        recordVisit,
        toggleFavorite,
        clearRecents,
        isFavorite,
        recentItems,
        popularItems,
        defaultHomePage,
        setDefaultHomePage,
        setAuthzUnlocked,
        externalAuthzUnlocked,
    };
});
